import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import './Essentials.css';

const Essentials = ({ activeTab: propActiveTab }) => {
  const tabMapping = {
    essentials: 'todo',
    essentials_todo: 'todo',
    essentials_document: 'document',
    essentials_memos: 'memos',
    essentials_reminders: 'reminders',
    essentials_messages: 'messages',
    essentials_knowledge_base: 'knowledge_base',
    essentials_settings: 'settings',
  };
  const getMappedTab = (tab) => tabMapping[tab] || 'todo';

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const [activeTab, setActiveTab] = useState(getMappedTab(propActiveTab));
  const prevPropRef = React.useRef(propActiveTab);

  useEffect(() => {
    if (propActiveTab !== prevPropRef.current) {
      setActiveTab(getMappedTab(propActiveTab));
      prevPropRef.current = propActiveTab;
    }
  }, [propActiveTab]);

  const [loading, setLoading] = useState(false);
  const [todos, setTodos] = useState([]);
  const [showAddTodo, setShowAddTodo] = useState(false);
  const [todoForm, setTodoForm] = useState({ task: '', assigned_to: '', priority: 'low', start_date: '', estimated_hours: '', description: '' });
  const [todoSearch, setTodoSearch] = useState('');
  const [todoEntries, setTodoEntries] = useState('All');

  const [docs, setDocs] = useState([]);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docForm, setDocForm] = useState({ file: null, description: '' });
  const [docSearch, setDocSearch] = useState('');

  const [memos, setMemos] = useState([]);
  const [showAddMemo, setShowAddMemo] = useState(false);
  const [memoForm, setMemoForm] = useState({ heading: '', description: '' });
  const [memoSearch, setMemoSearch] = useState('');

  const [reminders, setReminders] = useState([]);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [reminderForm, setReminderForm] = useState({ event_name: '', repeat: 'one_time', date: '', start_time: '', end_time: '' });
  const [calDate, setCalDate] = useState(new Date());

  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [msgLocation, setMsgLocation] = useState('');

  const [kbItems, setKbItems] = useState([]);
  const [showAddKb, setShowAddKb] = useState(false);
  const [kbForm, setKbForm] = useState({ title: '', content: '', share_with: 'public' });

  const [settingsTab, setSettingsTab] = useState('leave');
  const [settingsForm, setSettingsForm] = useState({ leave_instructions: '', payroll_instructions: '', attendance_instructions: '', sales_target_instructions: '', essentials_instructions: '' });

  const renderPriorityBadge = (priority) => {
    const p = (priority || 'low').toLowerCase();
    const map = {
      low: { bg: '#ecfdf5', text: '#047857', label: 'Low' },
      medium: { bg: '#fffbeb', text: '#b45309', label: 'Medium' },
      high: { bg: '#fff7ed', text: '#c2410c', label: 'High' },
      urgent: { bg: '#fef2f2', text: '#b91c1c', label: 'Urgent' },
    };
    const c = map[p] || map.low;
    return (
      <span className="mfg-badge" style={{ background: c.bg, color: c.text }}>
        {c.label}
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    const s = (status || 'new').toLowerCase();
    const map = {
      new: { bg: '#eff6ff', text: '#1d4ed8', label: 'New' },
      in_progress: { bg: '#f3e8ff', text: '#7e22ce', label: 'In Progress' },
      on_hold: { bg: '#fef2f2', text: '#b91c1c', label: 'On Hold' },
      completed: { bg: '#ecfdf5', text: '#047857', label: 'Completed' },
    };
    const c = map[s] || map.new;
    return (
      <span className="mfg-badge" style={{ background: c.bg, color: c.text }}>
        {c.label}
      </span>
    );
  };

  const loadTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'todo') {
        const res = await apiService.getEssentialsTodos();
        const apiList = Array.isArray(res) ? res : (res?.data || res?.todos || []);
        if (apiList.length > 0) setTodos(apiList);
      } else if (activeTab === 'document') {
        const res = await apiService.getEssentialsDocuments();
        const apiList = Array.isArray(res) ? res : (res?.data || res?.documents || []);
        if (apiList.length > 0) setDocs(apiList);
      } else if (activeTab === 'memos') {
        const res = await apiService.getEssentialsMemos();
        const apiList = Array.isArray(res) ? res : (res?.data || res?.memos || []);
        if (apiList.length > 0) setMemos(apiList);
      } else if (activeTab === 'reminders') {
        const res = await apiService.getEssentialsReminders();
        const apiList = Array.isArray(res) ? res : (res?.data || res?.reminders || []);
        if (apiList.length > 0) setReminders(apiList);
      } else if (activeTab === 'messages') {
        const res = await apiService.getEssentialsMessages();
        const apiList = Array.isArray(res) ? res : (res?.data || res?.messages || []);
        if (apiList.length > 0) setMessages(apiList);
      } else if (activeTab === 'knowledge_base') {
        const res = await apiService.getEssentialsKB();
        const apiList = Array.isArray(res) ? res : (res?.data || res?.articles || []);
        if (apiList.length > 0) setKbItems(apiList);
      } else if (activeTab === 'settings') {
        const res = await apiService.getHrmSettings();
        if (res) setSettingsForm(prev => ({ ...prev, ...res }));
      }
    } catch (err) {
      /* console.warn('Essentials data fetch fallback:', err.message); */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  const handleSaveTodo = async () => {
    if (!todoForm.task) {
      toast.warning('Task title is required');
      return;
    }
    const newTodo = {
      id: Date.now(),
      task: todoForm.task,
      priority: todoForm.priority || 'low',
      status: 'new',
      start_date: todoForm.start_date || new Date().toISOString().slice(0, 10),
      estimated_hours: todoForm.estimated_hours || '-',
      description: todoForm.description || '',
      created_at: new Date().toISOString().slice(0, 10),
      assigned_to: todoForm.assigned_to || '-'
    };
    setTodos(prev => [newTodo, ...prev]);
    setShowAddTodo(false);
    setTodoForm({ task: '', assigned_to: '', priority: 'low', start_date: '', estimated_hours: '', description: '' });
    toast.success('Task created successfully');

    try {
      await apiService.createEssentialsTodo(todoForm);
    } catch (err) {
      /* console.error(err); */
    }
  };

  const handleDeleteTodo = async (id) => {
    setTodos(prev => prev.filter(x => x.id !== id));
    toast.success('Task deleted successfully');
    try {
      await apiService.deleteEssentialsTodo(id);
    } catch (err) {}
  };

  const handleSaveDoc = async () => {
    if (!docForm.file) {
      toast.warning('File is required');
      return;
    }
    const newDoc = {
      id: Date.now(),
      name: docForm.file ? docForm.file.name : 'Document',
      description: docForm.description || '',
      created_at: new Date().toISOString().slice(0, 10)
    };
    setDocs(prev => [newDoc, ...prev]);
    setShowAddDoc(false);
    setDocForm({ file: null, description: '' });
    toast.success('Document uploaded successfully');

    try {
      const formData = new FormData();
      formData.append('document', docForm.file);
      formData.append('description', docForm.description || '');
      await apiService.uploadEssentialsDocument(formData);
    } catch (err) {}
  };

  const handleDeleteDoc = async (id) => {
    setDocs(prev => prev.filter(x => x.id !== id));
    toast.success('Document deleted successfully');
    try {
      await apiService.deleteEssentialsDocument(id);
    } catch (err) {}
  };

  const handleSaveMemo = async () => {
    if (!memoForm.heading) {
      toast.warning('Heading is required');
      return;
    }
    const newMemo = {
      id: Date.now(),
      heading: memoForm.heading,
      description: memoForm.description || '',
      created_at: new Date().toISOString().slice(0, 10)
    };
    setMemos(prev => [newMemo, ...prev]);
    setShowAddMemo(false);
    setMemoForm({ heading: '', description: '' });
    toast.success('Memo created successfully');

    try {
      await apiService.createEssentialsMemo(memoForm);
    } catch (err) {}
  };

  const handleDeleteMemo = async (id) => {
    setMemos(prev => prev.filter(x => x.id !== id));
    toast.success('Memo deleted successfully');
    try {
      await apiService.deleteEssentialsMemo(id);
    } catch (err) {}
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.event_name || !reminderForm.date) {
      toast.warning('Event name and date are required');
      return;
    }
    const newReminder = {
      id: Date.now(),
      event_name: reminderForm.event_name,
      repeat: reminderForm.repeat,
      date: reminderForm.date,
      start_time: reminderForm.start_time,
      end_time: reminderForm.end_time
    };
    setReminders(prev => [newReminder, ...prev]);
    setShowAddReminder(false);
    setReminderForm({ event_name: '', repeat: 'one_time', date: '', start_time: '', end_time: '' });
    toast.success('Reminder created successfully');

    try {
      await apiService.createEssentialsReminder(reminderForm);
    } catch (err) {}
  };

  const handleSendMessage = async () => {
    if (!msgInput.trim()) return;
    const newMsg = { id: Date.now(), message: msgInput, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, newMsg]);
    const inputVal = msgInput;
    setMsgInput('');
    toast.success('Message sent');

    try {
      await apiService.sendEssentialsMessage({ message: inputVal, location_id: msgLocation });
    } catch (err) {}
  };

  const handleSaveKB = async () => {
    if (!kbForm.title) {
      toast.warning('Title is required');
      return;
    }
    const newKb = {
      id: Date.now(),
      title: kbForm.title,
      content: kbForm.content || '',
      share_with: kbForm.share_with || 'public',
      created_at: new Date().toISOString().slice(0, 10)
    };
    setKbItems(prev => [newKb, ...prev]);
    setShowAddKb(false);
    setKbForm({ title: '', content: '', share_with: 'public' });
    toast.success('Knowledge Base article created successfully');

    try {
      await apiService.createEssentialsKB(kbForm);
    } catch (err) {}
  };

  const handleDeleteKB = async (id) => {
    setKbItems(prev => prev.filter(x => x.id !== id));
    toast.success('Article deleted successfully');
    try {
      await apiService.deleteEssentialsKB(id);
    } catch (err) {}
  };

  const handleSaveSettings = async () => {
    try {
      await apiService.updateHrmSettings(settingsForm);
      toast.success('Essentials settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const tabs = [
    { k: 'todo', l: 'To Do List', i: 'fas fa-list-check' },
    { k: 'document', l: 'Documents', i: 'fas fa-folder-open' },
    { k: 'memos', l: 'Memos', i: 'fas fa-sticky-note' },
    { k: 'reminders', l: 'Reminders Calendar', i: 'fas fa-bell' },
    { k: 'messages', l: 'Team Messages', i: 'fas fa-comments' },
    { k: 'knowledge_base', l: 'Knowledge Base', i: 'fas fa-book-open' },
    { k: 'settings', l: 'Settings', i: 'fas fa-sliders' },
  ];

  const renderStatsHeader = () => (
    <div className="mfg-stats-grid">
      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5' }}>
          <i className="fas fa-tasks" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Active Tasks</div>
          <div className="mfg-stat-value">{todos.length}</div>
          <div className="mfg-stat-subtext">To-do items logged</div>
        </div>
      </div>

      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
          <i className="fas fa-file-alt" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Documents</div>
          <div className="mfg-stat-value">{docs.length}</div>
          <div className="mfg-stat-subtext">Shared file uploads</div>
        </div>
      </div>

      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
          <i className="fas fa-sticky-note" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Memos</div>
          <div className="mfg-stat-value">{memos.length}</div>
          <div className="mfg-stat-subtext">Internal announcements</div>
        </div>
      </div>

      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
          <i className="fas fa-book" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">KB Articles</div>
          <div className="mfg-stat-value">{kbItems.length}</div>
          <div className="mfg-stat-subtext">Knowledge base items</div>
        </div>
      </div>
    </div>
  );

  const Table = ({ headers, rows, loading, emptyTitle = "No records found", onAdd }) => (
    <div className="mfg-table-wrap">
      <table className="mfg-table">
        <thead>
          <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={headers.length} style={{ textAlign: 'center', padding: '3rem' }}><i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: '#4f46e5' }} /></td></tr>
          ) : !rows.length ? (
            <tr>
              <td colSpan={headers.length}>
                <div className="mfg-empty-state">
                  <div className="mfg-empty-icon-box">
                    <i className="fas fa-inbox" />
                  </div>
                  <div className="mfg-empty-title">{emptyTitle}</div>
                  <div className="mfg-empty-desc">There are currently no records found for this section. Click below to add a new entry.</div>
                  {onAdd && (
                    <button className="mfg-btn mfg-btn-primary" onClick={onAdd}>
                      <i className="fas fa-plus" /> Add New Record
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => <td key={j}>{cell}</td>)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderCalendar = () => {
    const year = calDate.getFullYear();
    const month = calDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const monthName = calDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '0.75rem', minHeight: '85px' }}></div>);
    for (let d = 1; d <= daysInMonth; d++) {
      const isToday = new Date().toDateString() === new Date(year, month, d).toDateString();
      cells.push(
        <div key={d} style={{ background: isToday ? 'var(--etpos-warning-light, #fef3c7)' : 'var(--etpos-card, #ffffff)', padding: '0.75rem', minHeight: '85px', fontSize: '0.85rem', fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--etpos-warning-text, #b45309)' : 'var(--etpos-text, #0f172a)' }}>
          {d}
        </div>
      );
    }
    return (
      <div style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '16px', border: '1px solid var(--etpos-border, #e2e8f0)', padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <button className="mfg-btn mfg-btn-sm" onClick={() => setCalDate(new Date(year, month - 1, 1))}>&lt;</button>
            <button className="mfg-btn mfg-btn-sm" onClick={() => setCalDate(new Date())}>Today</button>
            <button className="mfg-btn mfg-btn-sm" onClick={() => setCalDate(new Date(year, month + 1, 1))}>&gt;</button>
          </div>
          <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--etpos-text, #0f172a)', fontWeight: 800 }}>{monthName}</h3>
          <button className="mfg-btn mfg-btn-primary" onClick={() => setShowAddReminder(true)}><i className="fas fa-plus" /> Add Reminder</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', background: 'var(--etpos-border, #e2e8f0)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '12px', overflow: 'hidden' }}>
          {days.map((d) => (
            <div key={d} style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '0.65rem', textAlign: 'center', fontSize: '0.78rem', fontWeight: 700, color: 'var(--etpos-text-muted, #475569)', textTransform: 'uppercase' }}>
              {d}
            </div>
          ))}
          {cells}
        </div>
      </div>
    );
  };

  return (
    <div className="mfg-page">
      {/* Page Header Banner */}
      <div className="mfg-page-header">
        <div className="mfg-page-header-title-wrap">
          <div className="mfg-page-icon">
            <i className="fas fa-check-double" />
          </div>
          <div>
            <h1>Essentials Hub</h1>
            <div className="mfg-page-header-subtitle">Manage tasks, team documents, memos, event reminders, messages, and knowledge base articles</div>
          </div>
        </div>
      </div>

      {/* Top Stats Overview Grid */}
      {renderStatsHeader()}

      {/* Top Navigation Bar */}
      <div className="mfg-tabs-nav">
        {tabs.map(t => (
          <button
            key={t.k}
            className={`mfg-tab-btn ${activeTab === t.k ? 'active' : ''}`}
            onClick={() => setActiveTab(t.k)}
          >
            <i className={t.i} />
            {t.l}
          </button>
        ))}
      </div>

      {/* Active Tab Content Card */}
      {activeTab === 'todo' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-list-check" style={{ color: '#4f46e5' }} /> To Do List
          </div>
          <div className="mfg-toolbar">
            <div className="mfg-toolbar-left">
              <div className="mfg-entries-group">
                <span>Show</span>
                <select value={todoEntries} onChange={e => setTodoEntries(e.target.value)}>
                  <option value="All">All</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="mfg-toolbar-right">
              <div className="mfg-search-box">
                <i className="fas fa-search mfg-search-icon" />
                <input
                  type="text"
                  className="mfg-search"
                  placeholder="Search tasks..."
                  value={todoSearch}
                  onChange={e => setTodoSearch(e.target.value)}
                />
              </div>
              <button className="mfg-btn mfg-btn-primary" onClick={() => setShowAddTodo(true)}>
                <i className="fas fa-plus" /> Add Task
              </button>
            </div>
          </div>

          <Table
            headers={['Added On', 'Task Id', 'Task', 'Priority', 'Status', 'Start Date', 'End Date', 'Est. Hours', 'Assigned By', 'Assigned To', 'Actions']}
            rows={todos.filter(x => !todoSearch || (x.task || '').toLowerCase().includes(todoSearch.toLowerCase())).map(x => [
              x.created_at || '-',
              <span style={{ fontWeight: 600, color: '#4f46e5' }}>{x.task_id || x.id}</span>,
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{x.task}</span>,
              renderPriorityBadge(x.priority),
              renderStatusBadge(x.status),
              x.start_date || x.date || '-',
              x.end_date || '-',
              x.estimated_hours || '-',
              x.assigned_by?.name || '-',
              Array.isArray(x.users) ? x.users.map(u => u.name || u.first_name).join(', ') : (x.assigned_to || '-'),
              <div className="mfg-actions" key={x.id}>
                <button className="mfg-action-btn delete" onClick={() => handleDeleteTodo(x.id)} title="Delete"><i className="fas fa-trash-alt" /></button>
              </div>
            ])}
            loading={loading}
            emptyTitle="No Tasks Found"
            onAdd={() => setShowAddTodo(true)}
          />
        </div>
      )}

      {activeTab === 'document' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-folder-open" style={{ color: '#4f46e5' }} /> Documents Library
          </div>
          <div className="mfg-toolbar">
            <div className="mfg-toolbar-left">
              <div className="mfg-entries-group">
                <span>Show</span>
                <select><option>All</option></select>
                <span>entries</span>
              </div>
            </div>
            <div className="mfg-toolbar-right">
              <div className="mfg-search-box">
                <i className="fas fa-search mfg-search-icon" />
                <input type="text" className="mfg-search" placeholder="Search documents..." value={docSearch} onChange={e => setDocSearch(e.target.value)} />
              </div>
              <button className="mfg-btn mfg-btn-primary" onClick={() => setShowAddDoc(true)}>
                <i className="fas fa-plus" /> Upload Document
              </button>
            </div>
          </div>

          {showAddDoc && (
            <div className="mfg-filter-card" style={{ marginBottom: '1.25rem' }}>
              <div className="mfg-form-group">
                <label>Select Document File <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="file" className="mfg-input" onChange={(e) => setDocForm({...docForm, file: e.target.files[0]})} />
              </div>
              <div className="mfg-form-group">
                <label>Description</label>
                <textarea className="mfg-input" rows={3} value={docForm.description} onChange={(e) => setDocForm({...docForm, description: e.target.value})} />
              </div>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button className="mfg-btn mfg-btn-primary" onClick={handleSaveDoc}><i className="fas fa-check" /> Upload</button>
                <button className="mfg-btn" onClick={() => { setShowAddDoc(false); setDocForm({file:null,description:''}); }}>Cancel</button>
              </div>
            </div>
          )}

          <Table
            headers={['Document Name', 'Description', 'Uploaded Date', 'Actions']}
            rows={docs.filter(x => !docSearch || (x.name || x.description || '').toLowerCase().includes(docSearch.toLowerCase())).map(x => [
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{x.name || x.file_name || 'Document'}</span>,
              x.description || '-',
              x.created_at || '-',
              <div className="mfg-actions" key={x.id}>
                <button className="mfg-action-btn delete" onClick={() => handleDeleteDoc(x.id)} title="Delete"><i className="fas fa-trash-alt" /></button>
              </div>
            ])}
            loading={loading}
            emptyTitle="No Documents Uploaded"
            onAdd={() => setShowAddDoc(true)}
          />
        </div>
      )}

      {activeTab === 'memos' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-sticky-note" style={{ color: '#4f46e5' }} /> Internal Memos
          </div>
          <div className="mfg-toolbar">
            <div className="mfg-toolbar-left">
              <div className="mfg-entries-group">
                <span>Show</span>
                <select><option>All</option></select>
                <span>entries</span>
              </div>
            </div>
            <div className="mfg-toolbar-right">
              <div className="mfg-search-box">
                <i className="fas fa-search mfg-search-icon" />
                <input type="text" className="mfg-search" placeholder="Search memos..." value={memoSearch} onChange={e => setMemoSearch(e.target.value)} />
              </div>
              <button className="mfg-btn mfg-btn-primary" onClick={() => setShowAddMemo(true)}>
                <i className="fas fa-plus" /> Add Memo
              </button>
            </div>
          </div>

          {showAddMemo && (
            <div className="mfg-filter-card" style={{ marginBottom: '1.25rem' }}>
              <div className="mfg-form-group">
                <label>Heading <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="mfg-input" type="text" value={memoForm.heading} onChange={(e) => setMemoForm({...memoForm, heading: e.target.value})} />
              </div>
              <div className="mfg-form-group">
                <label>Description</label>
                <textarea className="mfg-input" rows={4} value={memoForm.description} onChange={(e) => setMemoForm({...memoForm, description: e.target.value})} />
              </div>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button className="mfg-btn mfg-btn-primary" onClick={handleSaveMemo}><i className="fas fa-check" /> Submit Memo</button>
                <button className="mfg-btn" onClick={() => { setShowAddMemo(false); setMemoForm({heading:'',description:''}); }}>Cancel</button>
              </div>
            </div>
          )}

          <Table
            headers={['Heading', 'Description', 'Created Date', 'Actions']}
            rows={memos.filter(x => !memoSearch || (x.heading || x.description || '').toLowerCase().includes(memoSearch.toLowerCase())).map(x => [
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{x.heading}</span>,
              x.description || '-',
              x.created_at || '-',
              <div className="mfg-actions" key={x.id}>
                <button className="mfg-action-btn delete" onClick={() => handleDeleteMemo(x.id)} title="Delete"><i className="fas fa-trash-alt" /></button>
              </div>
            ])}
            loading={loading}
            emptyTitle="No Memos Posted"
            onAdd={() => setShowAddMemo(true)}
          />
        </div>
      )}

      {activeTab === 'reminders' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-bell" style={{ color: '#4f46e5' }} /> Reminders Calendar
          </div>
          {renderCalendar()}
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-comments" style={{ color: '#4f46e5' }} /> Team Chat & Messages
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', height: '420px' }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', background: 'var(--etpos-bg, #f8fafc)', borderRadius: '12px', marginBottom: '1rem', border: '1px solid var(--etpos-border, #e2e8f0)' }}>
              {messages.length === 0 ? (
                <div className="mfg-empty-state" style={{ background: 'transparent', padding: '2rem' }}>
                  <div className="mfg-empty-icon-box"><i className="fas fa-comments" /></div>
                  <div className="mfg-empty-title">No Messages Yet</div>
                  <div className="mfg-empty-desc">Send a broadcast message to your team or selected location.</div>
                </div>
              ) : (
                messages.map(m => (
                  <div key={m.id} style={{ background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--etpos-text, #0f172a)', marginBottom: '0.25rem' }}>{m.message}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)' }}>{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center' }}>
              <input
                type="text"
                className="mfg-input"
                style={{ flex: 1 }}
                placeholder="Type message to team..."
                value={msgInput}
                onChange={(e) => setMsgInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
              />
              <select className="mfg-select" style={{ width: '160px' }} value={msgLocation} onChange={(e) => setMsgLocation(e.target.value)}>
                <option value="">All Locations</option>
                <option value="1">Warehouse</option>
                <option value="2">Store</option>
                <option value="3">Office</option>
              </select>
              <button className="mfg-btn mfg-btn-primary" onClick={handleSendMessage}><i className="fas fa-paper-plane" /> Send</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'knowledge_base' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-book-open" style={{ color: '#4f46e5' }} /> Knowledge Base Articles
          </div>
          <div className="mfg-toolbar">
            <div className="mfg-toolbar-left">
              <div className="mfg-entries-group">
                <span>Show</span>
                <select><option>All</option></select>
                <span>entries</span>
              </div>
            </div>
            <div className="mfg-toolbar-right">
              <button className="mfg-btn mfg-btn-primary" onClick={() => setShowAddKb(true)}>
                <i className="fas fa-plus" /> Add Article
              </button>
            </div>
          </div>

          {showAddKb && (
            <div className="mfg-filter-card" style={{ marginBottom: '1.25rem' }}>
              <div className="mfg-form-group">
                <label>Article Title <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="mfg-input" type="text" value={kbForm.title} onChange={(e) => setKbForm({...kbForm, title: e.target.value})} />
              </div>
              <div className="mfg-form-group">
                <label>Content</label>
                <textarea className="mfg-input" rows={5} value={kbForm.content} onChange={(e) => setKbForm({...kbForm, content: e.target.value})} />
              </div>
              <div className="mfg-form-group" style={{ maxWidth: '240px' }}>
                <label>Share Visibility</label>
                <select className="mfg-select" value={kbForm.share_with} onChange={(e) => setKbForm({...kbForm, share_with: e.target.value})}>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button className="mfg-btn mfg-btn-primary" onClick={handleSaveKB}><i className="fas fa-check" /> Publish Article</button>
                <button className="mfg-btn" onClick={() => { setShowAddKb(false); setKbForm({title:'',content:'',share_with:'public'}); }}>Cancel</button>
              </div>
            </div>
          )}

          <Table
            headers={['Title', 'Content Preview', 'Share Setting', 'Created Date', 'Actions']}
            rows={kbItems.map(x => [
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{x.title}</span>,
              x.content || '-',
              <span className="mfg-badge mfg-badge-primary">{x.share_with || 'public'}</span>,
              x.created_at || '-',
              <div className="mfg-actions" key={x.id}>
                <button className="mfg-action-btn delete" onClick={() => handleDeleteKB(x.id)} title="Delete"><i className="fas fa-trash-alt" /></button>
              </div>
            ])}
            loading={loading}
            emptyTitle="No Knowledge Base Articles"
            onAdd={() => setShowAddKb(true)}
          />
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-sliders" style={{ color: '#8b5cf6' }} /> Essentials Settings
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ width: '200px', flexShrink: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {['Leave','Payroll','Attendance','Sales Targets','Essentials'].map((s) => {
                  const key = s.toLowerCase().replace(/\s+/g, '_');
                  return (
                    <button
                      key={s}
                      className={`mfg-sub-tab ${settingsTab === key ? 'active' : ''}`}
                      style={{ width: '100%', justifyContent: 'flex-start', padding: '0.65rem 0.85rem' }}
                      onClick={() => setSettingsTab(key)}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: '280px', background: 'var(--etpos-bg, #f8fafc)', padding: '1.25rem', borderRadius: '12px', border: '1px solid var(--etpos-border, #e2e8f0)' }}>
              {settingsTab === 'leave' && (
                <div>
                  <div className="mfg-form-group" style={{ marginBottom: '1rem' }}>
                    <label>Leave Reference No. Prefix:</label>
                    <input className="mfg-input" type="text" placeholder="Leave Reference No. prefix" value={settingsForm.leave_instructions} onChange={(e) => setSettingsForm({...settingsForm, leave_instructions: e.target.value})} />
                  </div>
                  <div className="mfg-form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Leave Instructions:</label>
                    <textarea className="mfg-input" rows={6} value={settingsForm.leave_instructions} onChange={(e) => setSettingsForm({...settingsForm, leave_instructions: e.target.value})} placeholder="Leave Instructions..."></textarea>
                  </div>
                </div>
              )}
              {settingsTab === 'payroll' && (
                <div>
                  <div className="mfg-form-group" style={{ marginBottom: '1rem' }}>
                    <label>Payroll Reference No. Prefix:</label>
                    <input className="mfg-input" type="text" placeholder="Payroll Reference No. prefix" />
                  </div>
                  <div className="mfg-form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Payroll Instructions:</label>
                    <textarea className="mfg-input" rows={6} value={settingsForm.payroll_instructions} onChange={(e) => setSettingsForm({...settingsForm, payroll_instructions: e.target.value})} placeholder="Payroll Instructions..."></textarea>
                  </div>
                </div>
              )}
              {settingsTab === 'attendance' && (
                <div>
                  <div className="mfg-form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Attendance Instructions:</label>
                    <textarea className="mfg-input" rows={6} value={settingsForm.attendance_instructions} onChange={(e) => setSettingsForm({...settingsForm, attendance_instructions: e.target.value})} placeholder="Attendance Instructions..."></textarea>
                  </div>
                </div>
              )}
              {settingsTab === 'sales_targets' && (
                <div>
                  <div className="mfg-form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Sales Target Instructions:</label>
                    <textarea className="mfg-input" rows={6} value={settingsForm.sales_target_instructions} onChange={(e) => setSettingsForm({...settingsForm, sales_target_instructions: e.target.value})} placeholder="Sales Target Instructions..."></textarea>
                  </div>
                </div>
              )}
              {settingsTab === 'essentials' && (
                <div>
                  <div className="mfg-form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Essentials Instructions:</label>
                    <textarea className="mfg-input" rows={6} value={settingsForm.essentials_instructions} onChange={(e) => setSettingsForm({...settingsForm, essentials_instructions: e.target.value})} placeholder="Essentials Instructions..."></textarea>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <button className="mfg-btn mfg-btn-purple" onClick={handleSaveSettings}><i className="fas fa-save" /> Save Settings</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals Dialogs */}
      {showAddTodo && (
        <div className="mfg-modal-overlay" onClick={() => setShowAddTodo(false)}>
          <div className="mfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3>Add To Do Task</h3>
              <button className="mfg-modal-close" onClick={() => setShowAddTodo(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="mfg-modal-body">
              <div className="mfg-form-group">
                <label>Task Title <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="mfg-input" type="text" value={todoForm.task} onChange={(e) => setTodoForm({...todoForm, task: e.target.value})} />
              </div>
              <div className="mfg-form-group">
                <label>Assigned To:</label>
                <input className="mfg-input" type="text" value={todoForm.assigned_to} onChange={(e) => setTodoForm({...todoForm, assigned_to: e.target.value})} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="mfg-form-group">
                  <label>Priority <span style={{ color: '#ef4444' }}>*</span></label>
                  <select className="mfg-select" value={todoForm.priority} onChange={(e) => setTodoForm({...todoForm, priority: e.target.value})}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="mfg-form-group">
                  <label>Start Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="mfg-input" type="datetime-local" value={todoForm.start_date} onChange={(e) => setTodoForm({...todoForm, start_date: e.target.value})} />
                </div>
              </div>
              <div className="mfg-form-group">
                <label>Estimated Hours:</label>
                <input className="mfg-input" type="number" value={todoForm.estimated_hours} onChange={(e) => setTodoForm({...todoForm, estimated_hours: e.target.value})} />
              </div>
              <div className="mfg-form-group">
                <label>Description:</label>
                <textarea className="mfg-input" rows={4} value={todoForm.description} onChange={(e) => setTodoForm({...todoForm, description: e.target.value})} placeholder="Task details..."></textarea>
              </div>
            </div>
            <div className="mfg-modal-footer">
              <button className="mfg-btn" onClick={() => setShowAddTodo(false)}><i className="fas fa-times" /> Cancel</button>
              <button className="mfg-btn mfg-btn-primary" onClick={handleSaveTodo}><i className="fas fa-save" /> Save Task</button>
            </div>
          </div>
        </div>
      )}

      {showAddReminder && (
        <div className="mfg-modal-overlay" onClick={() => setShowAddReminder(false)}>
          <div className="mfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3>Add Reminder Event</h3>
              <button className="mfg-modal-close" onClick={() => setShowAddReminder(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="mfg-modal-body">
              <div className="mfg-form-group">
                <label>Event Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="mfg-input" type="text" value={reminderForm.event_name} onChange={(e) => setReminderForm({...reminderForm, event_name: e.target.value})} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="mfg-form-group">
                  <label>Repeat <span style={{ color: '#ef4444' }}>*</span></label>
                  <select className="mfg-select" value={reminderForm.repeat} onChange={(e) => setReminderForm({...reminderForm, repeat: e.target.value})}>
                    <option value="one_time">One time</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="mfg-form-group">
                  <label>Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="mfg-input" type="date" value={reminderForm.date} onChange={(e) => setReminderForm({...reminderForm, date: e.target.value})} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="mfg-form-group">
                  <label>Start Time <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="mfg-input" type="time" value={reminderForm.start_time} onChange={(e) => setReminderForm({...reminderForm, start_time: e.target.value})} />
                </div>
                <div className="mfg-form-group">
                  <label>End Time:</label>
                  <input className="mfg-input" type="time" value={reminderForm.end_time} onChange={(e) => setReminderForm({...reminderForm, end_time: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="mfg-modal-footer">
              <button className="mfg-btn" onClick={() => setShowAddReminder(false)}><i className="fas fa-times" /> Cancel</button>
              <button className="mfg-btn mfg-btn-primary" onClick={handleSaveReminder}><i className="fas fa-save" /> Save Reminder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Essentials;
